import { resolvePassMeta } from "./skillTestPass";

export type SubmissionKind = "problem" | "skill-test";

export type PlatformSubmission = {
  id: string;
  kind: SubmissionKind;
  title: string;
  status: string;
  statusLabel: string;
  submittedAt?: string;
  language?: string;
  runtimeMs?: number;
  memoryKb?: number;
  xpEarned?: number;
  passedTestCases?: number;
  totalTestCases?: number;
  score?: number;
  totalMarks?: number;
  timeTakenSeconds?: number;
  passed?: boolean;
  raw: any;
};

function toTime(value?: string | Date) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/** Practice/problem-explorer submissions only — excludes course & skill-test question usage. */
export function isPracticeProblemSubmission(submission: any): boolean {
  const usage: string[] = submission?.problemId?.usage ?? [];
  if (!usage.length) return true;
  if (usage.includes("practice")) return true;
  if (usage.includes("course") || usage.includes("skill_test")) return false;
  return true;
}

export function normalizeProblemSubmissions(submissions: any[]): PlatformSubmission[] {
  return (submissions ?? [])
    .filter(isPracticeProblemSubmission)
    .map((submission) => ({
    id: String(submission._id ?? submission.id),
    kind: "problem",
    title: submission.problemId?.title ?? "Problem",
    status: submission.status ?? "unknown",
    statusLabel: String(submission.status ?? "unknown").replace(/_/g, " "),
    submittedAt: submission.createdAt ?? submission.submittedAt,
    language: submission.programmingLanguage ?? submission.language,
    runtimeMs: submission.runtimeMs,
    memoryKb: submission.memoryKb,
    xpEarned: submission.xpEarned,
    passedTestCases: submission.passedTestCases,
    totalTestCases: submission.totalTestCases,
    raw: submission,
  }));
}

export function normalizeSkillTestSubmissions(attempts: any[]): PlatformSubmission[] {
  return (attempts ?? []).map((attempt) => {
    const { passed } = resolvePassMeta(attempt);
    return {
      id: String(attempt.id ?? attempt._id),
      kind: "skill-test",
      title: attempt.testTitle ?? attempt.testMeta?.title ?? "Skill Test",
      status: passed ? "passed" : "failed",
      statusLabel: passed ? "passed" : "failed",
      submittedAt: attempt.submittedAt,
      language: attempt.testMeta?.programmingLanguage ?? attempt.programmingLanguage,
      score: attempt.score ?? 0,
      totalMarks: attempt.totalMarks ?? attempt.testMeta?.totalMarks ?? 0,
      timeTakenSeconds: attempt.timeTakenSeconds,
      passed,
      raw: attempt,
    };
  });
}

export function mergePlatformSubmissions(problemSubmissions: any[], skillTestSubmissions: any[]): PlatformSubmission[] {
  return [
    ...normalizeProblemSubmissions(problemSubmissions),
    ...normalizeSkillTestSubmissions(skillTestSubmissions),
  ].sort((a, b) => toTime(b.submittedAt) - toTime(a.submittedAt));
}
